import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, IconButton, Modal, Tooltip } from "@mui/material";
import classNames from "classnames";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import CloseIcon from "@mui/icons-material/Close";
import { ModelTier, ModelTierState, selectModelTier, setModelTier } from "../redux/slices/modelTierSlice";
import { ActivePlanState } from "../assets/interfaces/interfaces";
import useEnhancedNavigate from "./Session/helpers";
import { logClickEvent } from "../analytics";

// composer model toggle: Pro locked until a bundle is owned; per-conversation tier rides on the stream payload.

export const LOCKED_PRO_HINT = "Add any bundle to unlock Pro";
const PRO_PREVIEW = [
  { name: "Pro AI Model", description: "Advanced reasoning with deeper analysis for complex market questions", Icon: PsychologyRoundedIcon },
  { name: "Deep Web Search", description: "Research answers with live sources from across the web", Icon: TravelExploreRoundedIcon },
  { name: "Stock Screener", description: "Filter 10K+ stocks by market cap, sector, and custom criteria", Icon: TrackChangesRoundedIcon },
];

// v2 carries `pro` explicitly; on the legacy shape any paid tier implies Pro
export const isProUnlocked = (plan: Partial<ActivePlanState["plan"]> | undefined): boolean => {
  if (!plan) return false;
  if (typeof plan.pro === "boolean") return plan.pro;
  if (Array.isArray(plan.modelOptions)) return plan.modelOptions.includes("pro");
  return !!plan.subscription_type && plan.subscription_type !== "Free";
};

type ModelToggleProps = {
  sessionId: string;
  disabled?: boolean;
};

const ModelToggle = ({ sessionId, disabled }: ModelToggleProps) => {
  // Plain useDispatch keeps this importable in jest without the store/axios graph
  const dispatch = useDispatch();
  const navigate = useEnhancedNavigate();
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);

  const tier = useSelector((state: { modelTier: ModelTierState }) => selectModelTier(state, sessionId));
  const plan = useSelector((state: { plan: ActivePlanState }) => state.plan.plan);
  const proUnlocked = isProUnlocked(plan);

  const segmentClasses = "flex items-center gap-1 h-6 px-3 rounded-full text-xs font-medium transition-colors";

  const selectTier = (next: ModelTier) => {
    if (next === "pro" && !proUnlocked) {
      setUnlockModalOpen(true);
      logClickEvent("model_toggle_unlock_modal_open");
      return;
    }
    dispatch(setModelTier({ sessionId, tier: next }));
    logClickEvent("model_toggle_select", { tier: next });
  };

  const goToBundleStore = () => {
    setUnlockModalOpen(false);
    logClickEvent("model_toggle_browse_bundles");
    navigate("/plans");
  };

  return (
    <>
      <div
        role="group"
        aria-label="Model"
        className="inline-flex items-center h-8 px-1 gap-0.5 rounded-full bg-tgpt-secondary-accentLight"
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => selectTier("base")}
          aria-pressed={tier === "base"}
          className={classNames(segmentClasses, {
            "bg-tgpt-secondary-accentLight text-tgpt-secondary-main": tier === "base",
            "text-mui-black-56 dark:text-mui-white-56": tier !== "base",
          })}
        >
          Basic
        </button>
        {proUnlocked
          ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() => selectTier("pro")}
                aria-pressed={tier === "pro"}
                className={classNames(segmentClasses, {
                  "bg-primary-blue-main text-white": tier === "pro",
                  "text-mui-black-56 dark:text-mui-white-56": tier !== "pro",
                })}
              >
                <AutoAwesomeOutlinedIcon className="!w-3 !h-3" />
                Pro
              </button>
            )
          : (
              <Tooltip title={LOCKED_PRO_HINT} placement="top" arrow>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => selectTier("pro")}
                  aria-label={LOCKED_PRO_HINT}
                  className={classNames(segmentClasses, "text-mui-black-38 dark:text-mui-white-38")}
                >
                  Pro
                  <LockOutlinedIcon data-testid="pro-lock-icon" className="!w-3 !h-3" />
                </button>
              </Tooltip>
            )}
      </div>

      <Modal open={unlockModalOpen} onClose={() => setUnlockModalOpen(false)} aria-labelledby="unlock-pro-modal">
        <div className="fixed inset-0 flex items-center justify-center">
          <div className="shadow w-modal-mobile sm:w-modal-md rounded-lg p-6 bg-white text-mui-black-87 dark:bg-background/paper-elevation-16 dark:text-white">
            <div className="flex flex-col gap-y-4 relative">
              <IconButton
                size="small"
                onClick={() => setUnlockModalOpen(false)}
                aria-label="Close"
                sx={{ position: "absolute", top: 0, right: 0 }}
              >
                <CloseIcon className="!w-5 !h-5" />
              </IconButton>
              <h6 id="unlock-pro-modal" className="font-medium pr-8">Unlock Pro & Pro Tools</h6>
              <p className="body2 text-mui-black-56 dark:text-mui-white-70">
                Included with any data bundle — from $2/month.
              </p>
              <ul className="flex flex-col gap-y-3">
                {PRO_PREVIEW.map(item => (
                  <li key={item.name} className="flex items-start gap-x-3">
                    <item.Icon className="!h-5 !w-5 mt-0.5 text-tgpt-secondary-main" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-xs text-mui-black-56 dark:text-mui-white-70">{item.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-x-2 pt-2">
                <Button variant="text" className="!text-transform-none" onClick={() => setUnlockModalOpen(false)}>
                  Not now
                </Button>
                <Button variant="contained" className="button-primary !text-transform-none" onClick={goToBundleStore}>
                  Browse bundles
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModelToggle;
